import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase-config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import "../../assets/styles/ClientShared.css";

const questions = [
  "Is the admin dashboard layout intuitive and easy to navigate?",
  "Can you view and manage all user and project data effectively?",
  "Do the edit and update functions work correctly?",
  "Are there any actions or pages that are not responsive or broken?",
  "Do notifications, statuses, and order tracking reflect the correct data?",
  "Is the portal fully functional on both desktop and mobile devices?",
  "Are you able to upload and view content where required?",
  "Is the search and filtering functionality working well?",
  "Is the UI consistent with the brand and cleanly styled?",
  "Did you encounter any unexpected errors or bugs?",
  "Are any key features missing that you'd expect in an admin panel?",
  "What improvements would make managing the site easier?",
  "Any other comments or feedback?"
];

const AdminPortalRevision = () => {
  const [revisionLink, setRevisionLink] = useState("");
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchAdminPortalRevisionData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) throw new Error("User document not found.");

        const userData = userSnap.data();
        const projectRef = doc(db, "projects", userData.projectId);
        const projectSnap = await getDoc(projectRef);

        if (projectSnap.exists()) {
          const projectData = projectSnap.data();
          setRevisionLink(projectData.revisionDetails?.adminPortalRevision?.link || "");
          setResponses(projectData.revisionDetails?.adminPortalRevision?.form || {});
        } else {
          throw new Error("Project document not found.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminPortalRevisionData();
  }, []);

  const handleInputChange = (question, value) => {
    setResponses((prev) => ({ ...prev, [question]: value }));
  };

  const saveResponses = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("User not authenticated.");

      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) throw new Error("User document not found.");

      const userData = userSnap.data();
      const projectRef = doc(db, "projects", userData.projectId);
      await updateDoc(projectRef, {
        "revisionDetails.adminPortalRevision.form": responses,
      });

      toast.success("Responses saved successfully!");
    } catch (err) {
      toast.error("Failed to save responses: " + err.message);
    }
  };

  return (
    <div className="cp-page cp-page--narrow">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Admin Portal Revision</h1>
      </div>

      {loading ? (
        <div className="cp-loading"><p>Loading...</p></div>
      ) : (
        <>
          {error && <p className="cp-message--error">{error}</p>}

          <div className="cp-section" style={{marginBottom:'20px'}}>
            <div className="cp-section-header"><h2 className="cp-section-title">Preview Link</h2></div>
            <div className="cp-section-body">
              {revisionLink ? (
                <a
                  href={revisionLink.startsWith('http') ? revisionLink : `https://${revisionLink}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cp-doc-link"
                  style={{fontSize:'0.9375rem'}}
                >
                  View Admin Portal →
                </a>
              ) : (
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',margin:0}}>No link available yet. Check back soon.</p>
              )}
            </div>
          </div>

          <div className="cp-section" style={{marginBottom:'20px'}}>
            <div className="cp-section-header"><h2 className="cp-section-title">Instructions</h2></div>
            <div className="cp-section-body">
              <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.7,marginTop:0}}>
                Please provide detailed and descriptive feedback for each question. Avoid vague responses. Below are examples of good and bad feedback:
              </p>
              <div style={{marginTop:'16px',display:'flex',flexDirection:'column',gap:'8px'}}>
                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'#16a34a',margin:0}}>Good</p>
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"When filtering projects by status, the filter dropdown resets every time a change is made. It should persist."</p>
                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--accent)',margin:'8px 0 0'}}>Avoid</p>
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"Filtering is broken."</p>
                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'#16a34a',margin:'8px 0 0'}}>Good</p>
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"The edit button on the user detail page doesn't respond after submitting an update. No confirmation appears."</p>
                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--accent)',margin:'8px 0 0'}}>Avoid</p>
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"Edit doesn't work."</p>
              </div>
            </div>
          </div>

          <div className="cp-section" style={{marginBottom:'20px'}}>
            <div className="cp-section-header"><h2 className="cp-section-title">Feedback Questions</h2></div>
            <div className="cp-section-body">
              {questions.map((question, index) => (
                <div key={index} className="cp-form-group">
                  <label>{question}</label>
                  <textarea
                    value={responses[question] || ''}
                    onChange={e => handleInputChange(question, e.target.value)}
                    placeholder="Write your response here..."
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="cp-btn-row">
            <button className="cp-btn-primary" onClick={saveResponses}>Save Responses</button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPortalRevision;
